import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

const AuthContext = createContext(null);

// Default session timeout (in milliseconds) - will be overridden by settings
const DEFAULT_INACTIVITY_TIMEOUT = 25 * 60 * 1000; // 25 minutes
const WARNING_DURATION = 60 * 1000; // 1 minute warning before logout
const ACTIVITY_DEBOUNCE_MS = 1000; // Debounce activity events by 1 second

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to check if it's past midnight since login
const isPastMidnightSinceLogin = (loginTime) => {
  if (!loginTime) return false;
  
  const loginDate = new Date(loginTime);
  const now = new Date();
  
  // Get midnight of the day after login
  const midnightAfterLogin = new Date(loginDate);
  midnightAfterLogin.setDate(midnightAfterLogin.getDate() + 1);
  midnightAfterLogin.setHours(0, 0, 0, 0);
  
  return now >= midnightAfterLogin;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [loginTime, setLoginTime] = useState(localStorage.getItem('loginTime'));
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(60);
  const [inactivityTimeout, setInactivityTimeout] = useState(
    parseInt(localStorage.getItem('sessionTimeoutMs')) || DEFAULT_INACTIVITY_TIMEOUT
  );
  
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isWarningActiveRef = useRef(false);

  const logout = useCallback((isAutoLogout = false) => {
    // Set flag for auto logout due to inactivity
    if (isAutoLogout) {
      localStorage.setItem('wasAutoLoggedOut', 'true');
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('loginTime');
    localStorage.removeItem('sessionTimeoutMs');
    // Clear any leftover emulation state
    localStorage.removeItem('emulation_original_token');
    localStorage.removeItem('emulation_original_user');
    localStorage.removeItem('emulating_as');
    setToken(null);
    setUser(null);
    setLoginTime(null);
    setShowTimeoutWarning(false);
    isWarningActiveRef.current = false;
    setInactivityTimeout(DEFAULT_INACTIVITY_TIMEOUT);
    
    // Clear all timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  // Start the warning countdown and final logout timer
  const startWarningCountdown = useCallback(() => {
    // Prevent multiple warnings
    if (isWarningActiveRef.current) return;
    
    isWarningActiveRef.current = true;
    setShowTimeoutWarning(true);
    setTimeoutCountdown(60);
    
    // Countdown interval
    countdownIntervalRef.current = setInterval(() => {
      setTimeoutCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Final logout after warning duration
    warningTimerRef.current = setTimeout(() => {
      console.log('Session timed out due to inactivity');
      logout(true); // Pass true to indicate auto logout
    }, WARNING_DURATION);
  }, [logout]);

  // Reset the inactivity timer - only resets, doesn't hide warning
  const resetInactivityTimer = useCallback((timeoutMs) => {
    // Clear existing inactivity timer only (not warning timers)
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    
    // Start new inactivity timer
    inactivityTimerRef.current = setTimeout(() => {
      startWarningCountdown();
    }, timeoutMs);
  }, [startWarningCountdown]);

  // Extend session (called when user clicks "Continue" on warning)
  const extendSession = useCallback(() => {
    // Clear warning timers
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Hide warning and reset flag
    isWarningActiveRef.current = false;
    setShowTimeoutWarning(false);
    
    // Reset inactivity timer
    lastActivityRef.current = Date.now();
    resetInactivityTimer(inactivityTimeout);
  }, [resetInactivityTimer, inactivityTimeout]);

  // Track user activity - separate effect with proper debouncing
  useEffect(() => {
    if (!token) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      // Don't reset during warning period
      if (isWarningActiveRef.current) return;
      
      const now = Date.now();
      // Debounce: only reset if enough time has passed since last activity
      if (now - lastActivityRef.current > ACTIVITY_DEBOUNCE_MS) {
        lastActivityRef.current = now;
        resetInactivityTimer(inactivityTimeout);
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timer
    lastActivityRef.current = Date.now();
    resetInactivityTimer(inactivityTimeout);

    return () => {
      // Cleanup event listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      // Clear timers
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [token, inactivityTimeout, resetInactivityTimer]);

  // Check for midnight logout every minute
  useEffect(() => {
    if (!token || !loginTime) return;

    const checkMidnightLogout = () => {
      if (isPastMidnightSinceLogin(loginTime)) {
        console.log('Session expired at midnight - logging out');
        logout(true); // Pass true to indicate auto logout
      }
    };

    // Check immediately on mount
    checkMidnightLogout();

    // Then check every minute
    const interval = setInterval(checkMidnightLogout, 60000);

    return () => clearInterval(interval);
  }, [token, loginTime, logout]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        // Check midnight logout on init
        if (loginTime && isPastMidnightSinceLogin(loginTime)) {
          console.log('Session expired at midnight - logging out on init');
          logout(true); // Pass true to indicate auto logout
          setLoading(false);
          return;
        }

        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('loginTime');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token, loginTime, logout]);

  const completeLogin = async (newToken, userData, trustedDeviceToken) => {
    const now = new Date().toISOString();
    localStorage.removeItem('emulation_original_token');
    localStorage.removeItem('emulation_original_user');
    localStorage.removeItem('emulating_as');

    localStorage.setItem('token', newToken);
    localStorage.setItem('loginTime', now);
    if (trustedDeviceToken) {
      localStorage.setItem('trusted_device_token', trustedDeviceToken);
    }
    setToken(newToken);
    setLoginTime(now);
    setUser(userData);

    try {
      const settingsResponse = await axios.get(`${API}/settings/public`);
      const timeoutMinutes = settingsResponse.data?.session_timeout_minutes || 25;
      const timeoutMs = timeoutMinutes * 60 * 1000;
      localStorage.setItem('sessionTimeoutMs', timeoutMs.toString());
      setInactivityTimeout(timeoutMs);
    } catch (err) {
      localStorage.setItem('sessionTimeoutMs', DEFAULT_INACTIVITY_TIMEOUT.toString());
      setInactivityTimeout(DEFAULT_INACTIVITY_TIMEOUT);
    }

    return userData;
  };

  const login = async (email, password) => {
    const trustedDeviceToken = localStorage.getItem('trusted_device_token') || null;
    const response = await axios.post(`${API}/auth/login`, {
      email,
      password,
      trusted_device_token: trustedDeviceToken,
    });

    // If OTP is required, return the challenge instead of logging in
    if (response.data?.otp_required) {
      return {
        otp_required: true,
        otp_token: response.data.otp_token,
        masked_email: response.data.masked_email,
        resend_available_in: response.data.resend_available_in,
      };
    }

    // Otherwise (trusted device bypass), proceed directly
    const { token: newToken, user: userData, trusted_device_token: echoed } = response.data;
    await completeLogin(newToken, userData, echoed);
    return { otp_required: false, user: userData };
  };

  const verifyOtp = async (otpToken, code, rememberDevice) => {
    const response = await axios.post(`${API}/auth/verify-otp`, {
      otp_token: otpToken,
      code,
      remember_device: !!rememberDevice,
    });
    const { token: newToken, user: userData, trusted_device_token: deviceToken } = response.data;
    // Clear any previous trusted-device token if user did NOT opt-in this time
    if (!rememberDevice) {
      localStorage.removeItem('trusted_device_token');
    }
    await completeLogin(newToken, userData, deviceToken);
    return userData;
  };

  const resendOtp = async (otpToken) => {
    const response = await axios.post(`${API}/auth/resend-otp`, { otp_token: otpToken });
    return response.data; // { otp_token, masked_email, resend_available_in }
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login,
      verifyOtp,
      resendOtp,
      logout, 
      loading, 
      getAuthHeaders, 
      loginTime,
      showTimeoutWarning,
      timeoutCountdown,
      extendSession
    }}>
      {children}
    </AuthContext.Provider>
  );
};
